import useSWR from 'swr';
import {
  GetExperienceRecommendationEndpoint,
  ExperienceRecommendationStatus,
  LanguageCode,
} from '@kouto/types';
import { useRef, useState } from 'react';
import getListingRecommendations, {
  GetListingRecommendationsResponse,
} from './getListingRecommendations';

interface UseGetListingRecommendationsProps {
  recommendationId: string | null;
  maxPolls?: number;
  pollInterval?: number;
  language?: LanguageCode;
}

const useGetListingRecommendations = ({
  recommendationId,
  maxPolls = 15,
  pollInterval = 2000,
  language,
}: UseGetListingRecommendationsProps) => {
  const pollCountRef = useRef(0);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const url = !recommendationId
    ? null
    : GetExperienceRecommendationEndpoint.url({ recommendationId, language });

  const swr = useSWR<GetListingRecommendationsResponse>(
    url ? [url] : null,
    url ? ([fetchUrl]) => getListingRecommendations(fetchUrl) : null,
    {
      refreshInterval: (data) => {
        if (!data) return pollInterval;

        if (pollCountRef.current >= maxPolls) {
          console.warn('Max polling attempts reached:', maxPolls);
          setHasTimedOut(true);
          return 0;
        }

        if (
          data.status === ExperienceRecommendationStatus.COMPLETED ||
          data.status === ExperienceRecommendationStatus.FAILED
        ) {
          return 0;
        }

        pollCountRef.current += 1;
        return pollInterval;
      },

      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: pollInterval,

      onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
        if (retryCount >= maxPolls) {
          console.error('Max polling attempts reached:', maxPolls);
          setHasTimedOut(true);
          return;
        }

        if (error.status >= 400 && error.status < 500) return;

        const backoff = Math.min(pollInterval * 2, 2 ** retryCount * 1000);
        setTimeout(() => revalidate({ retryCount }), backoff);
      },
    },
  );

  const isCompleted =
    !recommendationId ||
    swr.data?.status === ExperienceRecommendationStatus.COMPLETED;
  const isFailed =
    swr.data?.status === ExperienceRecommendationStatus.FAILED || hasTimedOut;
  const isPolling =
    !!recommendationId &&
    !hasTimedOut &&
    (!swr.data ||
      swr.data?.status === ExperienceRecommendationStatus.STARTED ||
      swr.data?.status === ExperienceRecommendationStatus.IN_PROGRESS);

  return {
    ...swr,
    isPolling,
    isCompleted,
    isFailed,
    listings: swr.data?.listings ?? [],
  };
};

export default useGetListingRecommendations;
